import { useEffect, useState } from 'react';

import { apiFetch } from './client';
import { ApiProblem } from './problem';

/**
 * Les photos des comptes.
 *
 * POURQUOI PAS UN SIMPLE <img src="…">. La photo est servie derrière le jeton
 * d'accès, comme toute donnée d'un compte ; or le jeton vit en mémoire (D29) et
 * une balise <img> ne sait pas l'envoyer. On la charge donc par `apiFetch`, et
 * on l'affiche par une adresse `blob:` locale.
 *
 * LE CACHE est indexé par compte ET version : une photo changée change de
 * version, l'ancienne entrée n'est plus jamais demandée. Une même photo affichée
 * en vingt endroits (liste, barre du haut, fiche) n'est téléchargée qu'une fois.
 * Les adresses `blob:` ne sont pas libérées : quelques dizaines de vignettes de
 * 256 px pour la durée d'un onglet, c'est moins cher que de compter qui les
 * regarde encore.
 */

const cache = new Map<string, Promise<string | null>>();

function load(accountId: string, version: string): Promise<string | null> {
    const key = `${accountId}:${version}`;
    let pending = cache.get(key);

    if (pending === undefined) {
        pending = apiFetch(`/api/accounts/${encodeURIComponent(accountId)}/avatar?v=${encodeURIComponent(version)}`)
            .then(async (response) => (response.ok ? URL.createObjectURL(await response.blob()) : null))
            .catch(() => null);
        cache.set(key, pending);
    }

    return pending;
}

/** L'adresse affichable de la photo, ou null (pas de photo, pas encore chargée, refusée). */
export function useAvatarUrl(accountId: string | undefined, version: string | null | undefined): string | null {
    const [url, setUrl] = useState<{ key: string; url: string | null } | null>(null);
    const key = accountId === undefined || version == null ? null : `${accountId}:${version}`;

    useEffect(() => {
        if (key === null || accountId === undefined || version == null) {
            return undefined;
        }

        let abandoned = false;

        void load(accountId, version).then((loaded) => {
            if (!abandoned) {
                setUrl({ key, url: loaded });
            }
        });

        return () => {
            abandoned = true;
        };
    }, [key, accountId, version]);

    return key !== null && url?.key === key ? url.url : null;
}

/** Envoie la photo déjà recadrée (256 × 256). Rend la nouvelle version. */
export async function uploadAvatar(accountId: string, image: Blob): Promise<string> {
    const response = await apiFetch(`/api/accounts/${encodeURIComponent(accountId)}/avatar`, {
        method: 'PUT',
        headers: { 'Content-Type': image.type },
        body: image,
    });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    const { avatarVersion } = (await response.json()) as { avatarVersion: string };
    // La photo vient d'ici : inutile de la retélécharger pour l'afficher.
    cache.set(`${accountId}:${avatarVersion}`, Promise.resolve(URL.createObjectURL(image)));

    return avatarVersion;
}

export async function removeAvatar(accountId: string): Promise<void> {
    const response = await apiFetch(`/api/accounts/${encodeURIComponent(accountId)}/avatar`, { method: 'DELETE' });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }
}
